/**
 * Class to observe modal visibility changes and dispatch events accordingly.
 * This helps coordinate behaviors when modals are opened or closed.
 */
export class ModalVisibilityObserver {
    static readonly ModalClosedEventName: string = 'modalclosed';
    static readonly ModalOpenedEventName: string = 'modalopened';

    private readonly modalMutationObserver: MutationObserver;
    private observedModals: Set<string> = new Set();

    constructor() {
        this.modalMutationObserver = new MutationObserver(
            ModalVisibilityObserver.modalMutationCallback
        );
    }

    /**
     * Callback function for mutation observer
     * @param mutations - Array of mutation records
     */
    private static modalMutationCallback(
        mutations: Array<MutationRecord>
    ): void {
        for (const mutation of mutations) {
            if (
                mutation.type === 'attributes' &&
                mutation.attributeName === 'open'
            ) {
                const target = mutation.target as HTMLElement;

                try {
                    if (mutation.oldValue !== null) {
                        // Dialog was open and is now closed
                        target.dispatchEvent(
                            new Event(
                                ModalVisibilityObserver.ModalClosedEventName,
                                {
                                    bubbles: true
                                } as EventInit
                            )
                        );
                    } else {
                        // Dialog was closed and is now open
                        target.dispatchEvent(
                            new Event(
                                ModalVisibilityObserver.ModalOpenedEventName,
                                {
                                    bubbles: true
                                } as EventInit
                            )
                        );
                    }
                } catch (error) {
                    console.error(
                        'Error dispatching modal visibility event:',
                        error
                    );
                }
            }
        }
    }

    /**
     * Register a modal element to be observed for visibility changes
     * @param modalElementId - The ID of the modal element to observe
     * @returns boolean indicating whether registration was successful
     */
    register(modalElementId: string): boolean {
        try {
            const modal = document.getElementById(modalElementId);

            if (!modal) {
                console.warn(
                    `Modal element with ID "${modalElementId}" not found`
                );
                return false;
            }

            this.modalMutationObserver.observe(modal, {
                attributeFilter: ['open'],
                attributeOldValue: true
            });

            this.observedModals.add(modalElementId);
            return true;
        } catch (error) {
            console.error(
                `Error registering modal "${modalElementId}":`,
                error
            );
            return false;
        }
    }

    /**
     * Unregister all observed modals
     */
    unregister(): void {
        try {
            this.modalMutationObserver.disconnect();
            this.observedModals.clear();
        } catch (error) {
            console.error('Error unregistering modals:', error);
        }
    }

    /**
     * Unregister a specific modal element
     * @param modalElementId - The ID of the modal element to stop observing
     * @returns boolean indicating whether unregistration was successful
     */
    unregisterModal(modalElementId: string): boolean {
        try {
            const modal = document.getElementById(modalElementId);

            if (!modal) {
                console.warn(
                    `Modal element with ID "${modalElementId}" not found for unregistration`
                );
                return false;
            }

            // We need to recreate the observer without this modal
            this.modalMutationObserver.disconnect();
            this.observedModals.delete(modalElementId);

            // Reconnect for remaining modals
            for (const id of this.observedModals) {
                const remainingModal = document.getElementById(id);
                if (remainingModal) {
                    this.modalMutationObserver.observe(remainingModal, {
                        attributeFilter: ['open'],
                        attributeOldValue: true
                    });
                }
            }

            return true;
        } catch (error) {
            console.error(
                `Error unregistering modal "${modalElementId}":`,
                error
            );
            return false;
        }
    }
}
